import React, { useState, useEffect } from "react";
import { getNotificationsAction, sendNotificationToOneAction, getChurchUsersAction } from "../../store/Users/UsersAction";
import { deleteNotificationAction, updatedNotificationsAction } from "../../store/Notifications/NotificationAction";
import { useSelector, useDispatch } from "react-redux";
import CustomTable from "../../components/universal/CustomTable";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import ConfirmToast from "../../components/universal/ConfirmToast";

const NotificationsComponent = ({ church }) => {
  const dispatch = useDispatch();
  const notifications = useSelector((state) => state.user.notifications);
  const churchUsers = useSelector((state) => state.user.churchUsers);
  const { sendNotificationLoading, sendNotificationSuccess, sendNotificationError } = useSelector(state => state.user);

  const [reloadData, setReloadData] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showSendModal, setShowSendModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notificationForm, setNotificationForm] = useState({
    userAuthId: "",
    notification_title: "",
    notification_description: "",
  });

  /* ---------------- Table Headers Configuration ---------------- */
  const notificationHeaders = [
    {
      headerName: "Title",
      headerId: "notification_title",
    },
    {
      headerName: "Description",
      headerId: "notification_description",
    },
    {
      headerName: "Actions",
      headerId: "actions",
    },
  ];

  /* ---------------- Fetch Church Users ---------------- */
  const fetchChurchUsers = () => {
    if (!church?._id) {
      toast.error("Please select a church first");
      return;
    }

    dispatch(
      getChurchUsersAction({
        churchObjId: church._id,
        callback: (response) => {
                  }
      })
    );
  };

  /* ---------------- Fetch Notifications Function ---------------- */
  const fetchNotifications = (page, limit, searchCriteria = {}, callbackFn) => {
    const churchObjId = church?._id;

    if (!churchObjId) {
            if (callbackFn) {
        callbackFn({
          data: [],
          total: 0,
          pageNum: page,
        });
      }
      return;
    }

    dispatch(
      getNotificationsAction({
        churchObjId: churchObjId,
        page: page || 1,
        limit: limit || 10,
        search: searchCriteria.search || "",
        callback: (response) => {
          if (response?.type === 1) {
            const notificationsList = response?.data || [];

            const normalizedData = notificationsList.map((notification, index) => {
              const createdDate = notification?.createdDate;
              let formattedDate = "N/A";

              if (createdDate) {
                try {
                  const dateObj = new Date(createdDate);
                  formattedDate = dateObj.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                } catch (e) {
                                    formattedDate = createdDate;
                }
              }

              return {
                ...notification,
                notification_title: notification?.notification_title || `Notification ${index + 1}`,
                notification_description: notification?.notification_description || "No description available",
                createdDate: formattedDate,
              };
            });

            if (callbackFn) {
              callbackFn({
                data: normalizedData,
                total: response?.totalRecords || notificationsList.length || 0,
                pageNum: page,
              });
            }
          } else {
                        if (callbackFn) {
              callbackFn({
                data: [],
                total: 0,
                pageNum: page,
              });
            }
          }
        },
      })
    );
  };

  /* ---------------- Handle Send Notification ---------------- */
  const handleSendNotification = () => {
    if (!church?._id) {
      toast.error("Please select a church first");
      return;
    }

    if (!notificationForm.userAuthId) {
      toast.error("Please select a user");
      return;
    }

    if (!notificationForm.notification_title.trim()) {
      toast.error("Please enter notification title");
      return;
    }

    if (!notificationForm.notification_description.trim()) {
      toast.error("Please enter notification description");
      return;
    }

    const payload = {
      userAuthId: notificationForm.userAuthId,
      notification_title: notificationForm.notification_title,
      notification_description: notificationForm.notification_description,
      churchObjId: church._id, // Automatically sent from church._id
      callback: (response) => {
        if (response.type === 1) {
          toast.success("Notification sent successfully!");
          handleCloseModal();
          setReloadData(prev => !prev); // Refresh notifications list
        } else {
          toast.error("Failed to send notification: " + response.error);
        }
      }
    };

        dispatch(sendNotificationToOneAction(payload));
  };

  /* ---------------- Modal Handlers ---------------- */
  const handleOpenModal = () => {
    if (!church?._id) {
      toast.error("Please select a church first");
      return;
    }
    fetchChurchUsers();
    setShowSendModal(true);
  };

  const handleCloseModal = () => {
    setShowSendModal(false);
    // Reset form
    setNotificationForm({
      userAuthId: "",
      notification_title: "",
      notification_description: "",
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingId(null);
    setNotificationForm({
      userAuthId: "",
      notification_title: "",
      notification_description: "",
    });
  };

  const handleOpenEditModal = (row) => {
    setEditingId(row?._id || null);
    setNotificationForm({
      userAuthId: row?.userAuthId || "",
      notification_title: row?.notification_title || "",
      notification_description: row?.notification_description || "",
    });
    setShowEditModal(true);
  };

  const handleUpdateNotification = () => {
    if (!editingId) {
      toast.error("Notification ID is missing");
      return;
    }
    if (!notificationForm.notification_title.trim()) {
      toast.error("Please enter notification title");
      return;
    }
    if (!notificationForm.notification_description.trim()) {
      toast.error("Please enter notification description");
      return;
    }

    dispatch(
      updatedNotificationsAction({
        apiPayloadRequest: {
          _id: editingId,
          notification_title: notificationForm.notification_title,
          notification_description: notificationForm.notification_description,
        },
        callback: (response) => {
          if (response?.type === 1) {
            toast.success("Notification updated successfully!");
            handleCloseEditModal();
            setReloadData((prev) => !prev);
          } else {
            toast.error("Failed to update notification: " + response?.error);
          }
        },
      })
    );
  };

  const handleDeleteNotification = (row) => {
    const id = row?._id;
    if (!id) {
      toast.error("Notification ID is missing");
      return;
    }

    dispatch(
      deleteNotificationAction({
        apiPayloadRequest: { _id: id },
        callback: (res) => {
          if (res?.type === 1) {
            toast.success("Notification deleted successfully!");
            setReloadData((prev) => !prev);
          } else {
            toast.error("Failed to delete notification: " + res?.error);
          }
        },
      })
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNotificationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /* ---------------- Effects ---------------- */
  useEffect(() => {
    if (church?._id) {
      setReloadData(prev => !prev);
    }
  }, [church?._id]);

//   useEffect(() => {
//     if (sendNotificationSuccess) {
//       toast.success("Notification sent successfully!");
//     }
//   }, [sendNotificationSuccess]);

//   useEffect(() => {
//     if (sendNotificationError) {
//       toast.error(sendNotificationError);
//     }
//   }, [sendNotificationError]);

  return (
    <>
    {
      church?
    <div className="flex flex-col h-full w-full flex-1 rounded-2xl overflow-hidden">
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      <div className="flex h-full flex-1">
        <div className="flex flex-col h-full w-full bg-greyishV2 bg-opacity-50 md:border-l md:border-r border-greyish border-opacity-30 overflow-auto">
          <div className="flex gap-4 items-center justify-end h-[64px] px-4 border-b border-[#28292a]">

            <div className="flex items-center gap-5">
              <button
                className="text-yellowish bg-blackish font-medium text-[12px] whitespace-nowrap border border-yellowish rounded-xl py-2 px-6"
                onClick={handleOpenModal}
              >
                Send Notification
              </button>
            </div>
          </div>

          <div className="px-4 my-4 h-full">
            <div className="overflow-y-auto overflow-x-hidden px-2">
              <div className="flex flex-col gap-4 relative">
                <CustomTable
                  searchFor="Notifications"
                  heightDesktop="h-[65vh]"
                  width=""
                  nthChildWidth="[&>*:nth-child(1)]:w-[220px] [&>*:nth-child(2)]:w-[360px] [&>*:nth-child(3)]:w-[140px]"
                  notificationTable={true}
                  data={[]}
                  headers={notificationHeaders}
                  customDataFetch={true}
                  customDataFetchFunction={fetchNotifications}
                  reloadData={reloadData}
                  setReloadData={setReloadData}
                  reverseTable={true}
                  nthChildStyle=""
                  searchCriteria={{ search: searchValue }}
                  handleEditClick={(row) => {
                    handleOpenEditModal(row);
                  }}
                  handleDeleteClick={(row) => {
                    const options = {
                      customUI: ({ onClose }) => (
                        <ConfirmToast
                          title="Are you sure?"
                          message="You want to delete this notification?"
                          onClose={() => onClose()}
                          onSuccess={() => {
                            handleDeleteNotification(row);
                            onClose();
                          }}
                        />
                      ),
                      closeOnEscape: true,
                      closeOnClickOutside: true,
                      keyCodeForClose: [8, 32],
                      overlayClassName: "!bg-gray-500 !bg-opacity-75",
                    };
                    confirmAlert(options);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Notification Modal */}
      {showSendModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-70 z-40 transition-opacity duration-300"
            onClick={handleCloseModal}
          />

          {/* Modal */}
          <div
  className={`fixed top-1/2 right-0 h-[90vh] w-[90%] max-w-[90%] bg-blackish shadow-2xl z-[9999] transition-all duration-500 ease-in-out ${
    showSendModal
      ? 'translate-x-0 -translate-y-1/2'
      : 'translate-x-full -translate-y-1/2'
  }`}
>
            <div className="h-full flex flex-col">
              {/* Modal Header */}
              <div className="flex justify-between items-center h-[64px] px-4 border-b border-[#28292a]">
                <h3 className="text-white font-semibold text-lg">Send Notifications</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-greyish hover:text-white transition-colors duration-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 bg-blackish">
                <div className="space-y-6 max-w-full mx-auto">
                  {/* Select User Dropdown */}
                  <div className="w-full bg-blackish p-4 rounded-lg ">
                    <label className="text-[12px] text-greyish font-medium leading-4">
                      Select User *
                    </label>
                    <select
                      name="userAuthId"
                      value={notificationForm.userAuthId}
                      onChange={handleInputChange}
                      className="bg-[#111111] p-2 w-full text-white text-[14px] font-medium mt-2 leading-4 rounded-md h-10 border border-[#28292a]"
                      required
                    >
                      <option value="" className="text-greyish bg-[#111111]">Select a user</option>
                      {churchUsers?.map((user) => (
                        <option
                          key={user._id}
                          value={user.userAuthId}
                          className="bg-[#111111] text-white"
                        >
                          {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userFullName}
                        </option>
                      ))}
                      {churchUsers?.length === 0 && (
                        <option value="" disabled className="text-greyish bg-[#111111]">Loading users...</option>
                      )}
                    </select>
                    {churchUsers?.length === 0 && (
                      <p className="text-sm text-greyish mt-1">No users found for this church</p>
                    )}
                  </div>

                  {/* Notification Title */}
                  <div className="w-full bg-blackish p-4 rounded-lg ">
                    <label className="text-[12px] text-greyish font-medium leading-4">
                      Notification Title *
                    </label>
                    <input
                      type="text"
                      name="notification_title"
                      value={notificationForm.notification_title}
                      onChange={handleInputChange}
                      placeholder="Enter Notification Name"
                      className="bg-[#111111] p-2 w-full text-white text-[14px] font-medium mt-2 leading-4 rounded-md h-10 border border-[#28292a]"
                      required
                    />
                  </div>

                  {/* Notification Description */}
                  <div className="w-full bg-blackish p-4 rounded-lg">
                    <label className="text-[12px] text-greyish font-medium leading-4">
                      Notification Description *
                    </label>
                    <textarea
                      name="notification_description"
                      value={notificationForm.notification_description}
                      onChange={handleInputChange}
                      placeholder="Enter Notification Description"
                      rows="6"
                      className="bg-[#111111] p-2 w-full text-white text-[14px] font-medium mt-2 leading-4 rounded-md resize-none border border-[#28292a]"
                      required
                    />
                  </div>

                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-[#28292a] bg-blackish">
                <div className="flex justify-end items-center space-x-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-greyish text-greyish font-medium text-[12px] rounded-xl hover:bg-[#111111] transition duration-200 bg-[#111111]"
                    disabled={sendNotificationLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSendNotification}
                    disabled={sendNotificationLoading}
                    className="px-6 py-2 text-yellowish bg-blackish font-medium text-[12px] border border-yellowish rounded-xl hover:bg-yellowish hover:text-blackish disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                  >
                    {sendNotificationLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellowish mr-2 inline-block"></div>
                        Sending...
                      </>
                    ) : (
                      "Send Notification"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    :
    <div className="w-full h-full flex items-center justify-center text-gray-400 ">
     <p>Add Church/Ministry/Channel First</p>

    </div>
}

      {/* Edit Notification Modal */}
      {showEditModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-70 z-40 transition-opacity duration-300"
            onClick={handleCloseEditModal}
          />

          <div
            className={`fixed top-1/2 right-0 h-[80vh] w-[90%] max-w-[90%] bg-blackish shadow-2xl z-[9999] transition-all duration-500 ease-in-out ${
              showEditModal ? "translate-x-0 -translate-y-1/2" : "translate-x-full -translate-y-1/2"
            }`}
          >
            <div className="h-full flex flex-col">
              <div className="flex justify-between items-center h-[64px] px-4 border-b border-[#28292a]">
                <h3 className="text-white font-semibold text-lg">Edit Notification</h3>
                <button
                  onClick={handleCloseEditModal}
                  className="text-greyish hover:text-white transition-colors duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 bg-blackish">
                <div className="space-y-6 max-w-full mx-auto">
                  <div className="w-full bg-blackish p-4 rounded-lg">
                    <label className="text-[12px] text-greyish font-medium leading-4">
                      Notification Title *
                    </label>
                    <input
                      type="text"
                      name="notification_title"
                      value={notificationForm.notification_title}
                      onChange={handleInputChange}
                      placeholder="Enter Notification Name"
                      className="bg-[#111111] p-2 w-full text-white text-[14px] font-medium mt-2 leading-4 rounded-md h-10 border border-[#28292a]"
                      required
                    />
                  </div>

                  <div className="w-full bg-blackish p-4 rounded-lg">
                    <label className="text-[12px] text-greyish font-medium leading-4">
                      Notification Description *
                    </label>
                    <textarea
                      name="notification_description"
                      value={notificationForm.notification_description}
                      onChange={handleInputChange}
                      placeholder="Enter Notification Description"
                      rows="6"
                      className="bg-[#111111] p-2 w-full text-white text-[14px] font-medium mt-2 leading-4 rounded-md resize-none border border-[#28292a]"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-[#28292a] bg-blackish">
                <div className="flex justify-end items-center space-x-4">
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className="px-6 py-2 border border-greyish text-greyish font-medium text-[12px] rounded-xl hover:bg-[#111111] transition duration-200 bg-[#111111]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdateNotification}
                    className="px-6 py-2 text-yellowish bg-blackish font-medium text-[12px] border border-yellowish rounded-xl hover:bg-yellowish hover:text-blackish transition duration-200"
                  >
                    Update Notification
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NotificationsComponent;
